// FT-073: чистая логика MP lifecycle UI (отмена/смена тарифа — превью, подтверждение, ошибки).
// Без сетевого слоя и Vuetify — тестируется юнитами. Бэкенд-контракты:
//   cancel_preview      → { charge_now_cents, avg_cars, period:{elapsed_days,cycle_days} }
//   change_plan_preview → { trial, charge_now_cents, new_plan, downgrade_blocked, period }
//   ошибки              → { error: "downgrade_blocked"|"stripe_mp_target_eur_only"|
//                                   "stripe_mp_not_configured"|"stripe_mp_no_subscription" }
// charge_now_cents — ПОЛНАЯ проратированная сумма неполного периода (DEC-06), в евроцентах.

// Онбордённый MP-клиент: провайдер MP + заведённая подписка. Показываем cancel/change-через-Stripe.
export function isStripeMpClient(mc) {
  return !!mc && mc.payment_provider_preference === "stripe_managed_payments" && !!mc.stripe_mp_subscription_id;
}

// MP назначен, но подписки ещё нет (продуктовый триал) — смена тарифа бесплатна (€0, DB-only).
export function isMpPreSubscription(mc) {
  return !!mc && mc.payment_provider_preference === "stripe_managed_payments" && !mc.stripe_mp_subscription_id;
}

// Провайдер = MP (с подпиской или без) — для подмены балансового UI на управление подпиской.
export function isMpProvider(mc) {
  return !!mc && mc.payment_provider_preference === "stripe_managed_payments";
}

// Статус MP-подписки → {key (i18n), color (Vuetify)}. Маппинг как на бэке (MP_ACCESS_OK/SOFT).
export function mpSubscriptionStatus(status) {
  if (status === "active" || status === "trialing") return { key: "stripe_mp_lifecycle.status_active", color: "success" };
  if (status === "past_due" || status === "incomplete") return { key: "stripe_mp_lifecycle.status_past_due", color: "warning" };
  if (status === "canceled") return { key: "stripe_mp_lifecycle.status_canceled", color: "error" };
  return { key: "stripe_mp_lifecycle.status_none", color: "grey" };
}

// Сумма строки леджера → строка с копейками, БЕЗ валюты и знака.
//
// Инцидент 2026-09-08: суточное списание fix_plan с ценой, полученной делением месячной
// (2000 ₽/мес ÷ 30 = 66.66666666666667), печаталось в кабинете как есть — float колонки
// transfers.sum со всеми семнадцатью значащими цифрами. Соседний баланс на том же экране
// уже шёл через toFixed(2), форматирование забыли только в строках леджера.
//
// Именно 2 знака, а НЕ roundMoney: тот для RUB округляет до целого рубля (это верно для
// витрины месячной цены тарифа — там показывается оценка), а здесь копейки — фактическое
// движение денег по балансу, прятать их нельзя.
//
// Number() — суммы приходят и строкой (Paddle). Нечисловое отдаём как есть, чтобы вместо
// неожиданного значения в леджере не встал NaN.
export function formatLedgerAmount(value) {
  if (value === null || value === undefined) return "";
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : String(value);
}

// Евроценты → "€X.XX".
export function formatEuro(cents) {
  const n = Number(cents) || 0;
  return "€" + (n / 100).toFixed(2);
}

// Множитель суточный→месяц: price/min_payment_sum в тарифе указаны ЗА СУТКИ (day_period) → ×30;
// month_period/year_period — цена уже не суточная → ×1. Совпадает с MpBilling::PeriodAmount на бэке.
export function monthlyMultiplier(plan) {
  return plan && plan.payment_period === "day_period" ? 30 : 1;
}

// Денежное округление по валюте: RUB — до целого рубля (без дробных в UI);
// прочие (EUR/USD) — до 2 знаков. Используется во всех месячных пересчётах тарифа.
export function roundMoney(value, currency) {
  const v = Number(value) || 0;
  return currency === "RUB" ? Math.round(v) : Math.round(v * 100) / 100;
}

// Месячная смета MP: pay_as_you_go → round(техника) × цена × множитель, не ниже минимума;
// fix_plan → фикс-цена × множитель. ОЦЕНКА «при текущем числе единиц» (реально MP списывает
// по СРЕДНЕМУ за месяц) — отсюда «≈». Округление по валюте (RUB — целое, EUR/USD — 2 знака).
export function mpMonthlyEstimate(mc) {
  const p = mc && mc.plan;
  if (!p) return 0;
  const mult = monthlyMultiplier(p);
  const cur = p.currency;
  if (p.payment_type === "fix_plan") return roundMoney(Number(p.price) * mult, cur);
  const avg = Math.round(Number(mc.active_cars_count) || 0);
  const est = avg * (Number(p.price) || 0) * mult;
  return Math.max(roundMoney(est, cur), roundMoney((Number(p.min_payment_sum) || 0) * mult, cur));
}

// Текст подтверждения отмены. t(key, params) — i18n-функция ($t).
export function cancelConfirmText(preview, t) {
  if (!preview || !preview.charge_now_cents) {
    return t("stripe_mp_lifecycle.cancel_confirm_zero");
  }
  const period = preview.period || {};
  return t("stripe_mp_lifecycle.cancel_confirm", {
    amount: formatEuro(preview.charge_now_cents),
    elapsed: period.elapsed_days || 0,
    cycle: period.cycle_days || 0
  });
}

// Текст подтверждения смены тарифа (триал=бесплатно; live=проратация или 0).
export function changeConfirmText(preview, t) {
  if (preview && preview.trial) return t("stripe_mp_lifecycle.change_trial");
  if (!preview || !preview.charge_now_cents) return t("stripe_mp_lifecycle.change_zero");
  return t("stripe_mp_lifecycle.change_charge", { amount: formatEuro(preview.charge_now_cents) });
}

// downgrade-UX: сколько авто убрать (active − limit, не меньше 0).
export function downgradeMessage(ctx, t) {
  const active = Number(ctx && ctx.active_cars_count) || 0;
  const limit = Number(ctx && ctx.cars_limit) || 0;
  return t("stripe_mp_lifecycle.downgrade_blocked", { over: Math.max(0, active - limit), limit: limit });
}

// Места — второй ограничитель понижения (инцидент 2026-08-06): тариф не должен давать меньше
// мест, чем у компании активных сотрудников. Купленные extra_user бэкенд уже учёл в users_limit.
export function downgradeUsersMessage(ctx, t) {
  const active = Number(ctx && ctx.active_workers_count) || 0;
  const limit = Number(ctx && ctx.users_limit) || 0;
  return t("stripe_mp_lifecycle.downgrade_blocked_users", { over: Math.max(0, active - limit), limit: limit });
}

// Код ошибки API → i18n-ключ сообщения.
const ERROR_KEYS = {
  downgrade_blocked: "stripe_mp_lifecycle.err_downgrade",
  downgrade_blocked_users: "stripe_mp_lifecycle.err_downgrade_users",
  stripe_mp_target_eur_only: "stripe_mp_lifecycle.err_target_eur",
  stripe_mp_not_configured: "stripe_mp_lifecycle.err_not_configured",
  stripe_mp_no_subscription: "stripe_mp_lifecycle.err_no_subscription",
  // Holistic-ревью P2: precondition-коды (мисконфиг/гонка) — точная копи вместо generic.
  stripe_mp_disabled: "stripe_mp_lifecycle.err_disabled",
  stripe_mp_only: "stripe_mp_lifecycle.err_mp_only",
  stripe_mp_eur_only: "stripe_mp_lifecycle.err_eur_only"
};

export function errorTextKey(errorCode) {
  return ERROR_KEYS[errorCode] || "stripe_mp_lifecycle.err_generic";
}
